import React, { useState, useEffect } from 'react';
import { usePlatformAuth } from '@/context/PlatformAuthContext';
import PlatformLayout from '@/components/layout/PlatformLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Loader2, 
  MessageSquare, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  Mail, 
  Phone, 
  Building2, 
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  ArrowRight,
  RefreshCw,
  User,
  Globe,
  StickyNote
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Inquiries = () => {
  const { fetchWithAuth } = usePlatformAuth();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    loadInquiries();
    loadStats();
  }, [statusFilter]);

  const loadInquiries = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/inquiries/list?limit=100`;
      if (statusFilter && statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to load inquiries');
      
      const data = await response.json();
      setInquiries(data.inquiries || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/inquiries/stats`);
      if (!response.ok) throw new Error('Failed to load stats');
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const openInquiryDetail = (inquiry) => {
    setSelectedInquiry(inquiry);
    setNewStatus(inquiry.status);
    setNewNote('');
    setShowDetailDialog(true);
  };

  const updateInquiryStatus = async () => {
    if (!selectedInquiry) return;
    
    setUpdating(true);
    try {
      let url = `${API_URL}/api/inquiries/${selectedInquiry.inquiry_id}/status?status=${newStatus}`;
      if (newNote.trim()) {
        url += `&note=${encodeURIComponent(newNote.trim())}`;
      }
      
      const response = await fetch(url, { method: 'PATCH' });
      if (!response.ok) throw new Error('Failed to update inquiry');
      
      toast.success('Inquiry updated successfully');
      setShowDetailDialog(false);
      loadInquiries();
      loadStats();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const statusColors = {
    new: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    contacted: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    converted: 'bg-green-500/10 text-green-400 border-green-500/20',
    closed: 'bg-slate-500/10 text-slate-400 border-slate-500/20'
  };

  const statusIcons = {
    new: MessageSquare,
    contacted: Phone,
    converted: CheckCircle,
    closed: XCircle
  };

  const filteredInquiries = inquiries.filter(inq => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      inq.name?.toLowerCase().includes(searchLower) ||
      inq.email?.toLowerCase().includes(searchLower) ||
      inq.company?.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <PlatformLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Partnership Inquiries</h1>
            <p className="text-slate-400 mt-1">Manage leads from the landing page</p>
          </div>
          
          <Button 
            onClick={() => { loadInquiries(); loadStats(); }} 
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Total</p>
                    <p className="text-2xl font-bold text-white">{stats.total || 0}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">New</p>
                    <p className="text-2xl font-bold text-blue-400">{stats.by_status?.new || 0}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Contacted</p>
                    <p className="text-2xl font-bold text-yellow-400">{stats.by_status?.contacted || 0}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-yellow-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Converted</p>
                    <p className="text-2xl font-bold text-green-400">{stats.by_status?.converted || 0}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, or company..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              data-testid="inquiry-search"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px] bg-slate-800 border-slate-700 text-white">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all" className="text-white hover:bg-slate-700">All Status</SelectItem>
              <SelectItem value="new" className="text-white hover:bg-slate-700">New</SelectItem>
              <SelectItem value="contacted" className="text-white hover:bg-slate-700">Contacted</SelectItem>
              <SelectItem value="converted" className="text-white hover:bg-slate-700">Converted</SelectItem>
              <SelectItem value="closed" className="text-white hover:bg-slate-700">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Inquiries Table */}
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : filteredInquiries.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No inquiries found</p>
                <p className="text-sm text-slate-500 mt-1">
                  {statusFilter !== 'all' ? 'Try changing the filter' : 'Inquiries will appear here when visitors submit the contact form'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left p-4 text-sm font-medium text-slate-400">Contact</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-400">Company</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-400">Status</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-400">Date</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInquiries.map((inquiry) => {
                      const StatusIcon = statusIcons[inquiry.status] || MessageSquare;
                      return (
                        <tr 
                          key={inquiry.inquiry_id} 
                          className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors"
                        >
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                                <User className="w-5 h-5 text-slate-400" />
                              </div>
                              <div>
                                <p className="font-medium text-white">{inquiry.name}</p>
                                <p className="text-sm text-slate-400">{inquiry.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-slate-500" />
                              <span className="text-slate-300">{inquiry.company || '-'}</span>
                            </div>
                            {inquiry.phone && (
                              <div className="flex items-center gap-2 mt-1">
                                <Phone className="w-3 h-3 text-slate-500" />
                                <span className="text-xs text-slate-400">{inquiry.phone}</span>
                              </div>
                            )}
                          </td>
                          <td className="p-4">
                            <Badge className={`${statusColors[inquiry.status]} border`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {inquiry.status}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2 text-slate-400 text-sm">
                              <Calendar className="w-4 h-4" />
                              {formatDate(inquiry.created_at)}
                            </div>
                          </td>
                          <td className="p-4">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openInquiryDetail(inquiry)}
                              className="text-blue-400 hover:text-blue-300 hover:bg-slate-700"
                              data-testid={`view-inquiry-${inquiry.inquiry_id}`}
                            >
                              View
                              <ArrowRight className="w-4 h-4 ml-1" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-400" />
              Inquiry Details
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              View and manage this partnership inquiry
            </DialogDescription>
          </DialogHeader>
          
          {selectedInquiry && (
            <div className="space-y-6 py-4">
              {/* Contact Info */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
                  <div className="w-12 h-12 rounded-full bg-slate-600 flex items-center justify-center">
                    <User className="w-6 h-6 text-slate-300" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">{selectedInquiry.name}</p>
                    <p className="text-sm text-slate-400">{selectedInquiry.company || 'No company'}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 p-2 bg-slate-700/30 rounded">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <a href={`mailto:${selectedInquiry.email}`} className="text-sm text-blue-400 hover:underline">
                      {selectedInquiry.email}
                    </a>
                  </div>
                  {selectedInquiry.phone && (
                    <div className="flex items-center gap-2 p-2 bg-slate-700/30 rounded">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <a href={`tel:${selectedInquiry.phone}`} className="text-sm text-blue-400 hover:underline">
                        {selectedInquiry.phone}
                      </a>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Globe className="w-4 h-4" />
                  Source: {selectedInquiry.source}
                  <span className="mx-2">•</span>
                  <Calendar className="w-4 h-4" />
                  {formatDate(selectedInquiry.created_at)}
                </div>
              </div>

              {/* Status Update */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-slate-300">Update Status</label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="new" className="text-white">New</SelectItem>
                    <SelectItem value="contacted" className="text-white">Contacted</SelectItem>
                    <SelectItem value="converted" className="text-white">Converted</SelectItem>
                    <SelectItem value="closed" className="text-white">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Add Note */}
              <div className="space-y-3">
                <label className="text-sm font-medium text-slate-300">Add Note (optional)</label>
                <Textarea
                  placeholder="Add a follow-up note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 min-h-[80px]"
                />
              </div>

              {/* Existing Notes */}
              {selectedInquiry.notes && selectedInquiry.notes.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <StickyNote className="w-4 h-4" />
                    Previous Notes
                  </label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {selectedInquiry.notes.map((note, idx) => (
                      <div key={idx} className="p-2 bg-slate-700/50 rounded text-sm">
                        <p className="text-slate-300">{note.text}</p>
                        <p className="text-xs text-slate-500 mt-1">{formatDate(note.timestamp)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDetailDialog(false)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={updateInquiryStatus}
              disabled={updating}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="update-inquiry-btn"
            >
              {updating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PlatformLayout>
  );
};

export default Inquiries;
